import React, { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ImageUpload from "../components/ImageUpload";
import { Plus, Trash2 } from "lucide-react";

function BranchEditor({ branches, onChange }) {
  const add = () => onChange([...(branches || []), { name: "", address: "", phone: "" }]);
  const upd = (i, k, v) => onChange(branches.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const rem = (i) => onChange(branches.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>Branches</Label>
        <Button type="button" data-testid="add-branch-btn" variant="outline" size="sm" onClick={add} className="rounded-lg"><Plus className="w-4 h-4 mr-1" /> Add Branch</Button>
      </div>
      <div className="space-y-3">
        {(branches || []).map((b, i) => (
          <div key={i} data-testid={`branch-${i}`} className="border border-slate-200 rounded-xl p-3 grid sm:grid-cols-3 gap-2 relative">
            <Input data-testid={`branch-name-${i}`} value={b.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Branch name" className="h-10 rounded-lg" />
            <Input data-testid={`branch-address-${i}`} value={b.address || ""} onChange={(e) => upd(i, "address", e.target.value)} placeholder="Address" className="h-10 rounded-lg" />
            <div className="flex gap-2">
              <Input data-testid={`branch-phone-${i}`} value={b.phone || ""} onChange={(e) => upd(i, "phone", e.target.value)} placeholder="Phone" className="h-10 rounded-lg" />
              <button type="button" data-testid={`branch-remove-${i}`} onClick={() => rem(i)} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {(!branches || branches.length === 0) && <p className="text-sm text-slate-400">No branches added. The main address above is used by default.</p>}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const isCentre = user?.role === "radiology_centre";
  const isPharmacy = user?.role === "pharmacy";
  const [saving, setSaving] = useState(false);

  const [doc, setDoc] = useState({
    full_name: "", medical_reg_number: "", clinic_name: "", clinic_address: "", phone: "",
    signature_file_id: null, clinic_logo_file_id: null,
  });
  const [centre, setCentre] = useState({ name: "", address: "", phone: "", email: "", whatsapp: "", logo_file_id: null, branches: [] });

  useEffect(() => {
    if (user) {
      setDoc({
        full_name: user.full_name || "", medical_reg_number: user.medical_reg_number || "",
        clinic_name: user.clinic_name || "", clinic_address: user.clinic_address || "", phone: user.phone || "",
        signature_file_id: user.signature_file_id || null, clinic_logo_file_id: user.clinic_logo_file_id || null,
      });
    }
    if (isCentre) {
      api.get("/centres").then((r) => {
        const mine = r.data.find((c) => c.id === user.centre_id);
        if (mine) setCentre({ name: mine.name || "", address: mine.address || "", phone: mine.phone || "", email: mine.email || "", whatsapp: mine.whatsapp || "", logo_file_id: mine.logo_file_id || null, branches: mine.branches || [] });
      });
    }
    if (isPharmacy) {
      api.get("/pharmacies").then((r) => {
        const mine = r.data.find((c) => c.id === user.pharmacy_id);
        if (mine) setCentre({ name: mine.name || "", address: mine.address || "", phone: mine.phone || "", email: mine.email || "", whatsapp: mine.whatsapp || "", logo_file_id: mine.logo_file_id || null, license_number: mine.license_number || "", pharmacist_in_charge: mine.pharmacist_in_charge || "", branches: mine.branches || [] });
      });
    }
  }, [user]);

  const saveDoctor = async () => {
    setSaving(true);
    try { const { data } = await api.put("/auth/profile", doc); setUser(data.user); toast.success("Profile saved"); }
    catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };
  const saveCentre = async () => {
    setSaving(true);
    try { await api.put(`/centres/${user.centre_id}`, centre); toast.success("Centre profile saved"); }
    catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const setD = (k) => (e) => setDoc({ ...doc, [k]: e.target.value });
  const setCe = (k) => (e) => setCentre({ ...centre, [k]: e.target.value });

  if (isPharmacy) {
    const savePh = async () => {
      setSaving(true);
      try { await api.put(`/pharmacies/${user.pharmacy_id}`, centre); toast.success("Pharmacy profile saved"); }
      catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
    };
    return (
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Pharmacy Profile</h1>
        <p className="text-slate-500 mt-1">This information appears on prescriptions sent to your pharmacy.</p>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 mt-6 space-y-5">
          <div><Label>Pharmacy Name</Label><Input data-testid="ph-name-input" value={centre.name} onChange={setCe("name")} className="mt-1.5 h-12 rounded-xl" /></div>
          <div className="grid md:grid-cols-2 gap-5">
            <div><Label>Phone</Label><Input data-testid="ph-phone-input" value={centre.phone} onChange={setCe("phone")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>WhatsApp Number</Label><Input data-testid="ph-whatsapp-input" value={centre.whatsapp} onChange={setCe("whatsapp")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>Email</Label><Input data-testid="ph-email-input" value={centre.email} onChange={setCe("email")} className="mt-1.5 h-12 rounded-xl" /></div>
          </div>
          <div><Label>Address</Label><Textarea data-testid="ph-address-input" value={centre.address} onChange={setCe("address")} className="mt-1.5 rounded-xl" /></div>
          <ImageUpload label="Pharmacy Logo" testId="ph-logo-upload" value={centre.logo_file_id} onChange={(id) => setCentre({ ...centre, logo_file_id: id })} />
          <BranchEditor branches={centre.branches} onChange={(b) => setCentre({ ...centre, branches: b })} />
          <Button data-testid="save-pharmacy-btn" onClick={savePh} disabled={saving} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>
        </div>
      </div>
    );
  }

  if (isCentre) {
    return (
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Centre Profile</h1>
        <p className="text-slate-500 mt-1">This information appears on referrals sent to your centre.</p>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 mt-6 space-y-5">
          <div><Label>Centre Name</Label><Input data-testid="centre-name-input" value={centre.name} onChange={setCe("name")} className="mt-1.5 h-12 rounded-xl" /></div>
          <div className="grid md:grid-cols-2 gap-5">
            <div><Label>Phone</Label><Input data-testid="centre-phone-input" value={centre.phone} onChange={setCe("phone")} className="mt-1.5 h-12 rounded-xl" /></div>
            <div><Label>WhatsApp Number</Label><Input data-testid="centre-whatsapp-input" value={centre.whatsapp} onChange={setCe("whatsapp")} className="mt-1.5 h-12 rounded-xl" placeholder="e.g. 18685550101" /></div>
            <div><Label>Email</Label><Input data-testid="centre-email-input" value={centre.email} onChange={setCe("email")} className="mt-1.5 h-12 rounded-xl" /></div>
          </div>
          <div><Label>Address</Label><Textarea data-testid="centre-address-input" value={centre.address} onChange={setCe("address")} className="mt-1.5 rounded-xl" /></div>
          <ImageUpload label="Centre Logo" testId="centre-logo-upload" value={centre.logo_file_id} onChange={(id) => setCentre({ ...centre, logo_file_id: id })} />
          <BranchEditor branches={centre.branches} onChange={(b) => setCentre({ ...centre, branches: b })} />
          <Button data-testid="save-centre-btn" onClick={saveCentre} disabled={saving} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">My Profile</h1>
      <p className="text-slate-500 mt-1">Your details and branding appear on every referral and PDF.</p>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 mt-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div><Label>Full Name</Label><Input data-testid="profile-name" value={doc.full_name} onChange={setD("full_name")} className="mt-1.5 h-12 rounded-xl" /></div>
          <div><Label>Medical Registration Number</Label><Input data-testid="profile-medreg" value={doc.medical_reg_number} onChange={setD("medical_reg_number")} className="mt-1.5 h-12 rounded-xl" /></div>
          <div><Label>Clinic Name</Label><Input data-testid="profile-clinic" value={doc.clinic_name} onChange={setD("clinic_name")} className="mt-1.5 h-12 rounded-xl" /></div>
          <div><Label>Phone</Label><Input data-testid="profile-phone" value={doc.phone} onChange={setD("phone")} className="mt-1.5 h-12 rounded-xl" /></div>
        </div>
        <div><Label>Clinic Address</Label><Textarea data-testid="profile-address" value={doc.clinic_address} onChange={setD("clinic_address")} className="mt-1.5 rounded-xl" /></div>
        <div className="grid md:grid-cols-2 gap-5">
          <ImageUpload label="Digital Signature" testId="signature-upload" value={doc.signature_file_id} onChange={(id) => setDoc({ ...doc, signature_file_id: id })} />
          <ImageUpload label="Clinic Logo (optional)" testId="logo-upload" value={doc.clinic_logo_file_id} onChange={(id) => setDoc({ ...doc, clinic_logo_file_id: id })} />
        </div>
        <Button data-testid="save-profile-btn" onClick={saveDoctor} disabled={saving} className="rounded-xl h-12 px-6 bg-blue-600 hover:bg-blue-700">{saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save Profile</>}</Button>
      </div>
    </div>
  );
}
